import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Lightbulb,
  SearchCheck,
  Briefcase,
  Fingerprint,
  Cpu,
  Package,
  Cog,
  Megaphone,
  TrendingUp,
  Rocket,
  BarChart3,
  ArrowUpRight,
} from "lucide-react";
import { DetailModal } from "@/components/site/DetailModal";

const MODULES = [
  {
    icon: Lightbulb,
    title: "BUSINESS IDEATION",
    desc: "Identify opportunities, problems and business concepts with real market potential.",
    accent: "#FF8800",
    detail:
      "LaunchLab works with aspiring founders to discover business ideas worth building. We analyse market gaps, consumer pain points, emerging trends and the founder's own strengths to shape concepts that have genuine commercial potential — not just enthusiasm behind them.",
    bullets: [
      "Opportunity and market-gap discovery workshops",
      "Problem–solution fit analysis",
      "Founder–idea alignment assessment",
      "Concept shortlisting with commercial scoring",
    ],
  },
  {
    icon: SearchCheck,
    title: "MARKET VALIDATION",
    desc: "Study customers, competitors, demand and market opportunities before investing heavily.",
    accent: "#00B0FF",
    detail:
      "Before serious money goes in, evidence must come out. LaunchLab validates the idea against the real market — customer interviews, competitor mapping, demand testing and willingness-to-pay studies — so every major decision rests on data rather than optimism.",
    bullets: [
      "Customer discovery interviews and surveys",
      "Competitor and pricing landscape mapping",
      "Landing-page and pilot-offer demand tests",
      "Go / no-go validation report with metrics",
    ],
  },
  {
    icon: Briefcase,
    title: "BUSINESS MODEL",
    desc: "Design practical revenue models, pricing strategies and scalable business structures.",
    accent: "#00E676",
    detail:
      "A good idea fails with a bad model. LaunchLab designs the complete revenue architecture — who pays, what they pay for, how much, and how the numbers scale — producing a business model that is practical to run and attractive to grow.",
    bullets: [
      "Revenue stream and pricing strategy design",
      "Unit economics and margin modelling",
      "Cost structure and break-even planning",
      "Scalable business structure blueprint",
    ],
  },
  {
    icon: Fingerprint,
    title: "BRAND CREATION",
    desc: "Brand identity, name, logo, positioning, packaging and communication that stands apart.",
    accent: "#FF8800",
    detail:
      "LaunchLab builds brands, not just logos. From naming and identity to positioning, packaging and the voice of every communication, we create a brand system that earns trust instantly and stays memorable across every customer touchpoint.",
    bullets: [
      "Brand naming, logo and visual identity",
      "Positioning and category strategy",
      "Packaging and collateral design",
      "Brand voice and communication guidelines",
    ],
  },
  {
    icon: Cpu,
    title: "TECHNOLOGY",
    desc: "Websites, e-commerce platforms, mobile apps, business software, automation and AI-enabled solutions.",
    accent: "#00B0FF",
    detail:
      "Technology is the backbone of a modern business. LaunchLab designs and builds the complete digital stack — customer-facing platforms, internal software, automation and AI — engineered to scale with the venture instead of being rebuilt every year.",
    bullets: [
      "Business websites and e-commerce platforms",
      "iOS and Android mobile applications",
      "Custom business software and automation systems",
      "AI-enabled workflows and digital platforms",
    ],
  },
  {
    icon: Package,
    title: "PRODUCT DEVELOPMENT",
    desc: "Transform concepts into real products and services ready for the market.",
    accent: "#00E676",
    detail:
      "From prototype to production. LaunchLab manages the journey of turning a concept into a real, sellable product or service — sourcing, formulation, prototyping, testing and refinement — until it is genuinely ready for customers.",
    bullets: [
      "Product concept engineering and prototyping",
      "Sourcing and manufacturing tie-ups",
      "Quality testing and iteration cycles",
      "Service design for non-physical offerings",
    ],
  },
  {
    icon: Cog,
    title: "OPERATIONS",
    desc: "Practical systems for procurement, inventory, logistics, customer management, vendors and teams.",
    accent: "#FF8800",
    detail:
      "Businesses are won or lost in daily execution. LaunchLab builds the operating machinery — procurement, inventory, logistics, customer management, vendor and employee systems — so the business runs like an institution, not an improvisation.",
    bullets: [
      "Procurement and vendor management frameworks",
      "Inventory and logistics operating systems",
      "Customer management (CRM) processes",
      "Branch, team and SOP design",
    ],
  },
  {
    icon: Megaphone,
    title: "MARKETING",
    desc: "Digital growth systems — social media, performance marketing, content, influencers and acquisition.",
    accent: "#00B0FF",
    detail:
      "Demand must be manufactured, not awaited. LaunchLab creates complete digital growth systems — content engines, performance campaigns, social presence and influencer programs — that generate a predictable flow of customers month after month.",
    bullets: [
      "Social media presence and content systems",
      "Performance marketing and ad campaigns",
      "Influencer and partnership campaigns",
      "Customer acquisition funnels with analytics",
    ],
  },
  {
    icon: TrendingUp,
    title: "SALES",
    desc: "Customer acquisition and distribution strategies that convert demand into revenue.",
    accent: "#00E676",
    detail:
      "Marketing creates interest; sales converts it into revenue. LaunchLab designs the sales strategy — channels, pitch, pricing conversations, distribution and partnerships — so demand reliably turns into paying customers.",
    bullets: [
      "Sales strategy and conversion playbooks",
      "Distribution channel design",
      "Retail, B2B and partner network development",
      "Sales team structure and training",
    ],
  },
  {
    icon: Rocket,
    title: "BUSINESS LAUNCH",
    desc: "Coordinate every component required to take the business from concept to market.",
    accent: "#FF8800",
    detail:
      "Launch day is choreography. LaunchLab synchronises brand, product, technology, operations and marketing into one coordinated market entry — with launch campaigns, PR, channel readiness and support systems all going live together.",
    bullets: [
      "Go-to-market launch planning and coordination",
      "Launch campaigns and PR activation",
      "Channel and platform readiness checks",
      "Post-launch monitoring and rapid fixes",
    ],
  },
  {
    icon: BarChart3,
    title: "GROWTH",
    desc: "Improve revenue, customers, operations, technology, distribution and scalability.",
    accent: "#00B0FF",
    detail:
      "After launch, LaunchLab stays as a growth partner — analysing what works, fixing what doesn't, and systematically expanding revenue, customers, distribution, operations and technology until the business becomes truly scalable.",
    bullets: [
      "Revenue and customer growth programs",
      "Operational efficiency and cost optimisation",
      "Technology upgrades and automation depth",
      "Distribution expansion and scale planning",
    ],
  },
];

function CapabilityCard({ mod, index, onOpen }) {
  const ref = useRef(null);
  const [style, setStyle] = useState({});

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setStyle({
      transform: `perspective(900px) rotateY(${px * 9}deg) rotateX(${-py * 9}deg) translateY(-5px)`,
      transition: "transform 0.08s ease-out",
    });
  };
  const onLeave = () =>
    setStyle({ transform: "perspective(900px) rotateY(0deg) rotateX(0deg)", transition: "transform 0.5s ease-out" });

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (index % 3) * 0.1 }}
    >
      <button
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        onClick={() => onOpen(mod)}
        style={style}
        data-testid={`capability-card-${mod.title.toLowerCase().replace(/\s+/g, "-")}`}
        className="card-beam group relative w-full text-left rounded-2xl border border-slate-800 bg-[#0D111D]/90 p-6 sm:p-7 transition-colors duration-300 hover:border-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8800]"
      >
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"
          style={{ boxShadow: `inset 0 0 0 1px ${mod.accent}55, 0 0 35px ${mod.accent}18` }}
        />
        <div className="flex items-start justify-between">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110"
            style={{ background: `${mod.accent}14`, border: `1px solid ${mod.accent}33` }}
          >
            <mod.icon className="w-5 h-5" style={{ color: mod.accent }} strokeWidth={1.8} />
          </div>
          <ArrowUpRight className="w-4 h-4 text-slate-600 group-hover:text-white transition-colors mt-1" />
        </div>
        <h3 className="font-display text-lg sm:text-xl font-semibold tracking-wide text-white">{mod.title}</h3>
        <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{mod.desc}</p>
        <p className="mt-3 font-mono-tech text-[10px] tracking-[0.25em] uppercase" style={{ color: mod.accent }}>
          Click to explore
        </p>
      </button>
    </motion.div>
  );
}

export function Capabilities() {
  const [selected, setSelected] = useState(null);

  return (
    <section id="what-we-do" data-testid="capabilities-section" className="relative py-20 sm:py-28 lg:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,136,0,0.05),transparent_55%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-14"
        >
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">02 — Capabilities</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white" data-testid="capabilities-headline">
            FROM IDEA TO <span className="text-gradient-saffron">REAL BUSINESS</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed">
            Every capability a venture needs — engineered as one connected ecosystem. Select any module to see exactly what LaunchLab delivers.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {MODULES.map((m, i) => (
            <CapabilityCard key={m.title} mod={m} index={i} onOpen={setSelected} />
          ))}
        </div>
      </div>

      <DetailModal
        item={
          selected && {
            Icon: selected.icon,
            title: selected.title,
            kicker: "LaunchLab Capability",
            detail: selected.detail,
            bullets: selected.bullets,
            accent: selected.accent,
          }
        }
        onClose={() => setSelected(null)}
      />
    </section>
  );
}
